import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError, BACKEND_URL, getAccessToken } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CloudArrowDown, CloudArrowUp, Clock, ArrowCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

/** Compact "Backup & Restore" panel embedded in the profile dialog.
 *
 * Coach/admin ⇒ whole-gym download + latest-auto-backup summary + upload
 *               button to restore from a JSON file + rollback-to-latest.
 * Athlete     ⇒ just the "download my data" button.
 */
export default function BackupPanel() {
  const { user } = useAuth();
  const isCoach = user?.role === "admin" || user?.role === "coach";
  const isAdmin = user?.role === "admin";
  const fileRef = useRef(null);
  const [latest, setLatest] = useState(null);
  const [busy, setBusy] = useState(null); // "download" | "snapshot" | "restore-latest" | "restore-upload"
  const [confirmOpen, setConfirmOpen] = useState(null); // "latest" | { snapshot, counts } for upload

  useEffect(() => {
    if (!isCoach) return;
    (async () => {
      try {
        const { data } = await api.get("/admin/backup/latest");
        setLatest(data);
      } catch { /* ignore */ }
    })();
  }, [isCoach]);

  // Direct download requests need to go through the browser so the file
  // save dialog fires. We attach the bearer token via a fetch → blob →
  // anchor click. Keeps the file name from the Content-Disposition header.
  const download = async (path, fallbackName) => {
    const token = getAccessToken();
    const res = await fetch(`${BACKEND_URL}/api${path}`, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`Download failed (${res.status})`);
    const cd = res.headers.get("Content-Disposition") || "";
    const m = /filename="([^"]+)"/.exec(cd);
    const filename = m ? m[1] : fallbackName;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const doDownloadWhole = async () => {
    setBusy("download");
    try { await download("/admin/backup/download", "healthspan-backup.json"); toast.success("Backup downloaded"); }
    catch (e) { toast.error(String(e.message || e)); }
    finally { setBusy(null); }
  };

  const doDownloadMine = async () => {
    setBusy("download");
    try { await download("/me/backup/download", "my-healthspan.json"); toast.success("Your data downloaded"); }
    catch (e) { toast.error(String(e.message || e)); }
    finally { setBusy(null); }
  };

  const doTakeSnapshot = async () => {
    setBusy("snapshot");
    try {
      const { data } = await api.post("/admin/backup/snapshot");
      setLatest({ exists: true, ...data });
      toast.success("Snapshot stored");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const doRestoreLatest = async () => {
    setBusy("restore-latest");
    try {
      await api.post("/admin/backup/restore-latest");
      toast.success("Restored — reloading");
      setTimeout(() => window.location.reload(), 800);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); setConfirmOpen(null); }
  };

  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const text = await file.text();
      const snapshot = JSON.parse(text);
      if (snapshot?.scope !== "global") {
        toast.error("That file isn't a whole-gym backup");
        return;
      }
      // Dry-run first so we can show the counts before wiping data.
      const { data } = await api.post("/admin/backup/restore?dry_run=1", snapshot);
      setConfirmOpen({ snapshot, counts: data.counts });
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't read that backup file");
    }
  };

  const commitUploadRestore = async () => {
    if (!confirmOpen?.snapshot) return;
    setBusy("restore-upload");
    try {
      await api.post("/admin/backup/restore", confirmOpen.snapshot);
      toast.success("Restored from uploaded backup — reloading");
      setTimeout(() => window.location.reload(), 800);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); setConfirmOpen(null); }
  };

  return (
    <div className="space-y-3" data-testid="backup-panel">
      <div className="hs-label">Backup & restore</div>

      {isCoach ? (
        <div className="space-y-3 rounded-xl border border-line p-4">
          {latest?.exists ? (
            <div className="flex items-center gap-2 text-xs text-ink-3" data-testid="backup-latest-info">
              <Clock size={14} />
              <span>
                Last auto-backup: <b className="text-ink-2">{new Date(latest.created_at).toLocaleString()}</b>
                {" · "}
                {latest.counts?.users || 0} users · {latest.counts?.programs || 0} programs · {latest.counts?.workout_logs || 0} logs
              </span>
            </div>
          ) : (
            <div className="text-xs text-ink-3">No auto-backup yet — takes one every night at 3am EST.</div>
          )}

          <div className="grid sm:grid-cols-2 gap-2">
            <Button
              type="button"
              variant="outline"
              className="rounded-xl h-11 justify-start"
              disabled={busy === "download"}
              onClick={doDownloadWhole}
              data-testid="backup-download-all-btn"
            >
              <CloudArrowDown size={18} className="mr-2" weight="regular" />
              {busy === "download" ? "Preparing…" : "Download whole-gym backup"}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="rounded-xl h-11 justify-start"
              disabled={busy === "snapshot"}
              onClick={doTakeSnapshot}
              data-testid="backup-take-snapshot-btn"
            >
              <Clock size={18} className="mr-2" weight="regular" />
              {busy === "snapshot" ? "Snapping…" : "Take snapshot now"}
            </Button>
            {isAdmin && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-xl h-11 justify-start"
                  onClick={() => fileRef.current?.click()}
                  data-testid="backup-upload-btn"
                >
                  <CloudArrowUp size={18} className="mr-2" weight="regular" />
                  Restore from file…
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-xl h-11 justify-start text-terracotta hover:bg-terracotta/10 hover:text-terracotta"
                  disabled={!latest?.exists || busy === "restore-latest"}
                  onClick={() => setConfirmOpen("latest")}
                  data-testid="backup-restore-latest-btn"
                >
                  <ArrowCounterClockwise size={18} className="mr-2" weight="regular" />
                  Roll back to last night's backup
                </Button>
              </>
            )}
          </div>
          <input ref={fileRef} type="file" accept="application/json,.json" className="hidden" onChange={onFilePicked} data-testid="backup-file-input" />
          <p className="text-[11px] text-ink-3 leading-snug">
            Restore replaces every athlete, coach, program, workout log, package, and attendance row with the ones in the backup. Anything created after the backup is <b>permanently lost</b>. Admin-only.
          </p>
        </div>
      ) : (
        <div className="space-y-2 rounded-xl border border-line p-4">
          <p className="text-xs text-ink-3">Download every workout, program, and check-in tied to your account as a JSON file. Keep it safe — you can hand it back to a coach to restore your data if anything goes wrong.</p>
          <Button
            type="button"
            variant="outline"
            className="rounded-xl h-11 w-full sm:w-auto"
            disabled={busy === "download"}
            onClick={doDownloadMine}
            data-testid="backup-download-mine-btn"
          >
            <CloudArrowDown size={18} className="mr-2" weight="regular" />
            {busy === "download" ? "Preparing…" : "Download my data"}
          </Button>
        </div>
      )}

      <AlertDialog open={!!confirmOpen} onOpenChange={(v) => !v && setConfirmOpen(null)}>
        <AlertDialogContent className="rounded-2xl" data-testid="backup-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Restore this backup?</AlertDialogTitle>
            <AlertDialogDescription>
              Every current athlete, program, log, and payment will be replaced with the contents of this backup. This cannot be undone.
              {confirmOpen && typeof confirmOpen === "object" && confirmOpen.counts && (
                <span className="mt-3 block font-data text-xs bg-muted2 rounded-lg p-2">
                  {Object.entries(confirmOpen.counts).map(([k, v]) => (
                    <span key={k} className="block">{k}: {v}</span>
                  ))}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl" data-testid="backup-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmOpen === "latest" ? doRestoreLatest : commitUploadRestore}
              className="rounded-xl bg-terracotta hover:bg-terracotta-hover text-white hover:text-white"
              data-testid="backup-confirm-restore"
            >
              Restore now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
